import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { editInfoSchema, EditInfoFormData } from "./schemas/editInfoSchema";
import { useUpdateUserInfo } from "./api";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

export default function EditInfo() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = location.state?.user;
  const [serverError, setServerError] = useState("");
  const updateUserInfoMutation = useUpdateUserInfo();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditInfoFormData>({
    resolver: zodResolver(editInfoSchema),
    defaultValues: {
      username: user?.username || "",
      email: user?.email || "",
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        username: user.username || "",
        email: user.email || "",
      });
    }
  }, [user, reset]);

  const queryClient = useQueryClient();

  const onSubmit = (data: EditInfoFormData) => {
    setServerError("");

    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }

    updateUserInfoMutation.mutate(data, {
      onSuccess: (response: any) => {
        if (response.pending) {
          // If backend says email change needs verification
          toast.info("Please verify your new email address.", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: true,
          });
          // Optionally navigate to a "verify new email" page or open modal
          navigate("/profile/verify-new-email");
        } else {
          // Username only (or username/email both but no verification needed)
          toast.success("Information updated successfully", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: true,
          });
          // Optionally navigate to profile
          queryClient.invalidateQueries({ queryKey: ["get-user-info"] });
          navigate("/profile");
        }
      },
      onError: (err: any) => {
        setServerError(
          err.response?.data?.message || "An error occurred while updating info"
        );
      },
    });
  };

  useEffect(() => {
    if (serverError) {
      toast.error(serverError);
    }
  }, [serverError]);

  return (
    <div className='flex justify-center mt-32'>
      <div className='flex-col border-solid border-2 border-[#D9D9D9] w-3/4 rounded-md flex items-center justify-center p-6 space-y-4 sm:w-3/4 md:w-1/2 lg:w-1/3'>
        <h1 className='text-lg font-bold'>Edit Your Personal Information</h1>
        <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
          <div className='flex flex-col w-full mb-5'>
            <p className='mb-1'>Username</p>
            <input
              type='text'
              {...register("username")}
              className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
              placeholder='Username'
            />
            {errors.username && (
              <span className='text-red-500 text-xs -mb-4'>
                {errors.username.message}
              </span>
            )}
          </div>
          <div className='flex flex-col w-full mb-16'>
            <p className='mb-1'>Email</p>
            <input
              type='text'
              {...register("email")}
              className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
              placeholder='Email'
            />
            {errors.email && (
              <span className='text-red-500 text-xs -mb-4'>
                {errors.email.message}
              </span>
            )}
          </div>
          <div className='flex w-full justify-between space-x-2 mt-4'>
            <Link to='/profile' className='w-1/2'>
              <button
                type='button'
                className='bg-[#E3E3E3] text-black rounded-md w-full py-2 hover:bg-[#D9D9D9]'
              >
                Cancel
              </button>
            </Link>
            <button
              type='submit'
              className='bg-[#FF5F02] text-white rounded-md w-1/2 py-2 hover:bg-[#FF4C00]'
            >
              {updateUserInfoMutation.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
